// src/components/backgroundcheck/BackgroundCheckAssignment.jsx
import React, { useState, useEffect } from 'react';
import { backgroundCheckAPI } from '../../services/backgroundCheckAPI';
import './style/BackgroundCheckAssignment.css';

const BackgroundCheckAssignment = ({ candidate, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [scope, setScope] = useState(['criminal']); // Default required scope

  const scopeOptions = [
    { id: 'criminal', label: 'Criminal History Check', required: true },
    { id: 'employment', label: 'Employment Verification', required: false },
    { id: 'education', label: 'Education Verification', required: false },
    { id: 'credit', label: 'Credit Check', required: false },
    { id: 'drug', label: 'Drug Screening', required: false },
    { id: 'reference', label: 'Reference Check', required: false },
  ];

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      const agenciesData = await backgroundCheckAPI.getAgencies();
      setAgencies(agenciesData);
    } catch (err) {
      setError(err.message || 'Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleScopeChange = (scopeId, checked) => {
    const option = scopeOptions.find(opt => opt.id === scopeId);
    
    if (checked) {
      setScope(prev => [...prev, scopeId]);
    } else {
      // Don't allow removing required items
      if (!option.required) {
        setScope(prev => prev.filter(id => id !== scopeId));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAgency) {
      setError('Please select an agency');
      return;
    }
    
    if (scope.length === 0) {
      setError('Please select at least one background check scope');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // If candidate prop is provided, use it; otherwise, this will be opened from candidate selection
      const candidateUserId = candidate?.userId || candidate?.id;
      const candidateProfileId = candidate?.profileId;

      await backgroundCheckAPI.assign(
        candidateUserId,
        candidateProfileId,
        selectedAgency,
        scope
      );

      onSuccess('Background check assigned successfully!');
    } catch (err) {
      setError(err.message || 'Failed to assign background check');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>Assign Background Check</h2>
          <button onClick={onClose} className="modal-close">
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠</span>
              {error}
            </div>
          )}

          {candidate && (
            <div className="candidate-summary">
              <h3>Candidate Information</h3>
              <p>
                <strong>Name:</strong> {candidate.firstName} {candidate.lastName}
              </p>
              <p>
                <strong>Email:</strong> {candidate.email}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Agency Selection */}
            <div className="form-group">
              <label htmlFor="agency" className="form-label">
                Select Background Check Agency *
              </label>
              <select
                id="agency"
                value={selectedAgency}
                onChange={(e) => setSelectedAgency(e.target.value)}
                className="form-control"
                required
                disabled={loading}
              >
                <option value="">-- Select Agency --</option>
                {agencies.map((agency) => (
                  <option key={agency.id} value={agency.id}>
                    {agency.profile?.firstName} {agency.profile?.lastName} ({agency.email})
                  </option>
                ))}
              </select>
              {agencies.length === 0 && !loading && (
                <p className="form-help-text error-text">
                  No agencies available. Please register an agency first.
                </p>
              )}
            </div>

            {/* Scope Selection */}
            <div className="form-group">
              <label className="form-label">Background Check Scope *</label>
              <div className="scope-checkboxes">
                {scopeOptions.map((option) => (
                  <label key={option.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={scope.includes(option.id)}
                      onChange={(e) => handleScopeChange(option.id, e.target.checked)}
                      disabled={option.required || loading}
                    />
                    <span>
                      {option.label}
                      {option.required && <span className="required-tag">(Required)</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="modal-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || agencies.length === 0}
              >
                {loading ? 'Assigning...' : 'Assign Background Check'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BackgroundCheckAssignment;
